import React, { useEffect, useRef } from "react"
import { composeSx } from "../theme/composeSx"
import {
  Box,
  ButtonBase,
  Stack,
  Typography,
  type ButtonBaseProps,
} from "@mui/material"
import { AccentDot } from "../atoms/AccentDot"
import { typeScale } from "../theme/tokens"
import { CircularAudioVisualizer } from "./CircularAudioVisualizer"
import { resolveViraPalette } from "../theme/resolveViraPalette"

export type StartSessionStatus =
  | "idle"
  | "recording"
  | "paused"
  | "uploading"
  | "processing"

export type StartSessionButtonProps = Omit<
  ButtonBaseProps,
  "children" | "onClick"
> & {
  label?: string
  status?: StartSessionStatus
  onStart?: (event: React.MouseEvent<HTMLElement>) => void
  onPause?: () => void
  onResume?: () => void
  onStop?: () => void
  /** Registers the live elapsed-time element while recording/paused. */
  registerElapsedTimeElement?: (element: HTMLElement) => () => void
  /**
   * Canvas for the circular mic visualizer. Recorder paints frequency bins
   * each animation frame via `paintCircularAudioVisualizer`.
   */
  audioVisualizerCanvasRef?: React.MutableRefObject<HTMLCanvasElement | null>
  /**
   * @deprecated Use `audioVisualizerCanvasRef`.
   */
  audioActivityIndicatorRef?: React.MutableRefObject<HTMLCanvasElement | null>
}

function ElapsedTime({
  registerElapsedTimeElement,
}: {
  registerElapsedTimeElement?: (element: HTMLElement) => () => void
}) {
  const ref = useRef<HTMLSpanElement | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || !registerElapsedTimeElement) return undefined
    return registerElapsedTimeElement(el)
  }, [registerElapsedTimeElement])
  return <span ref={ref}>00:00</span>
}

/**
 * Sidebar session control under the client name.
 * Idle → start; recording/paused → timer + pause/resume + end.
 */
export function StartSessionButton({
  label = "Start session",
  status = "idle",
  disabled,
  onStart,
  onPause,
  onResume,
  onStop,
  registerElapsedTimeElement,
  audioVisualizerCanvasRef,
  audioActivityIndicatorRef,
  sx,
  ...rest
}: StartSessionButtonProps) {
  const visualizerRef = audioVisualizerCanvasRef ?? audioActivityIndicatorRef

  if (status === "uploading" || status === "processing") {
    return (
      <Box
        sx={composeSx(
          (theme) => ({
            width: "100%",
            minHeight: 48,
            borderRadius: "8px",
            backgroundColor: resolveViraPalette(theme).lavender,
            border: `1px solid ${resolveViraPalette(theme).lavenderBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 1.5,
            gap: 1,
          }),
          sx,
        )}
        aria-live="polite"
      >
        <AccentDot size={7} />
        <Typography
          sx={(theme) => ({
            fontSize: typeScale.meta,
            fontWeight: 600,
            color: resolveViraPalette(theme).lavenderText,
          })}
        >
          {status === "uploading"
            ? "Transcript uploading…"
            : "Transcript processing…"}
        </Typography>
      </Box>
    )
  }

  if (status === "recording" || status === "paused") {
    return (
      <Stack spacing={1} sx={{ width: "100%" }}>
        <Box
          sx={{
            width: "100%",
            borderRadius: "8px",
            border: "1px solid #F5C9A6",
            backgroundColor: "#FDF1E8",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            px: 1.5,
            py: 1.5,
          }}
          aria-live="polite"
        >
          <CircularAudioVisualizer
            canvasRef={visualizerRef}
            paused={status === "paused"}
            size={104}
            label={
              status === "paused"
                ? "Microphone paused"
                : "Live microphone level"
            }
          />
          <Typography
            sx={{
              fontSize: typeScale.meta,
              fontWeight: 700,
              color: "#9A4B12",
            }}
          >
            {status === "paused" ? "Paused" : "Recording"} ·{" "}
            <ElapsedTime
              registerElapsedTimeElement={registerElapsedTimeElement}
            />
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <ButtonBase
            onClick={status === "paused" ? onResume : onPause}
            sx={{
              flex: 1,
              minHeight: 36,
              borderRadius: "8px",
              border: (theme) =>
                `1px solid ${resolveViraPalette(theme).lavenderBorder}`,
              backgroundColor: (theme) => resolveViraPalette(theme).lavender,
              fontSize: typeScale.meta,
              fontWeight: 600,
              color: (theme) => resolveViraPalette(theme).lavenderText,
            }}
          >
            {status === "paused" ? "Resume" : "Pause"}
          </ButtonBase>
          <ButtonBase
            onClick={onStop}
            sx={{
              flex: 1,
              minHeight: 36,
              borderRadius: "8px",
              border: "1px solid #F5C9A6",
              backgroundColor: "#FDF1E8",
              fontSize: typeScale.meta,
              fontWeight: 600,
              color: "#9A4B12",
            }}
          >
            End
          </ButtonBase>
        </Stack>
      </Stack>
    )
  }

  return (
    <ButtonBase
      disabled={disabled}
      onClick={onStart}
      sx={composeSx(
        (theme) => ({
          width: "100%",
          height: 48,
          borderRadius: "8px",
          backgroundColor: resolveViraPalette(theme).lavender,
          border: `1px solid ${resolveViraPalette(theme).lavenderBorder}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          opacity: disabled ? 0.5 : 1,
        }),
        sx,
      )}
      {...rest}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <AccentDot size={7} />
        <Typography
          sx={(theme) => ({
            fontSize: typeScale.body,
            fontWeight: 500,
            color: resolveViraPalette(theme).lavenderText,
          })}
        >
          {label}
        </Typography>
      </Stack>
    </ButtonBase>
  )
}

/** @deprecated Prefer CircularAudioVisualizer. */
export { CircularAudioVisualizer as AudioActivityIndicator } from "./CircularAudioVisualizer"
